import { useEffect, useMemo, useRef, useState } from 'react';
import { useEditorStore } from '../lib/editor-store.js';
import { extractDocumentText } from '../lib/pdf-text.js';
import { findMatches, matchRects, type PageText, type SearchMatch } from '../lib/search.js';
import { scrollToPageIndex } from '../lib/scroll-to-page.js';

/**
 * In-document text search. Extracts per-page text from the loaded PDF (via
 * pdf.js, lazily + cached per byte buffer), finds case-insensitive matches, and
 * lets the user step through them — each jump scrolls the matching page into
 * view. Searches the text encoded in the PDF; pending unsaved add-text edits are
 * not searchable until saved + reopened (noted in the UI).
 */
interface Props {
  readonly onClose: () => void;
}

// Cache extracted text per byte buffer so reopening the panel is instant.
const textCache = new WeakMap<Uint8Array, PageText[]>();

export function SearchPanel({ onClose }: Props): React.ReactElement {
  const doc = useEditorStore((s) => s.doc);
  const setSearchHighlight = useEditorStore((s) => s.setSearchHighlight);
  const bytes = doc?.originalBytes ?? null;

  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [pageTexts, setPageTexts] = useState<PageText[] | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `active` is the focused match; `jumped` is false until the user navigates,
  // so the first ↓/Enter lands on match 1 rather than skipping to match 2.
  const [active, setActive] = useState(0);
  const [jumped, setJumped] = useState(false);

  // Extract (once) when the document changes; reuse the cache on reopen.
  useEffect(() => {
    if (!bytes) return;
    const cached = textCache.get(bytes);
    if (cached) {
      setPageTexts(cached);
      return;
    }
    let cancelled = false;
    setExtracting(true);
    setError(null);
    extractDocumentText(bytes)
      .then((pages) => {
        if (cancelled) return;
        textCache.set(bytes, pages);
        setPageTexts(pages);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setExtracting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bytes]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const matches = useMemo<SearchMatch[]>(
    () => (pageTexts ? findMatches(pageTexts, query) : []),
    [pageTexts, query],
  );

  // A new query resets the cursor (without auto-scrolling on every keystroke).
  useEffect(() => {
    setActive(0);
    setJumped(false);
  }, [query]);

  // Clear the on-page highlight when the panel closes.
  useEffect(() => () => setSearchHighlight(null), [setSearchHighlight]);

  function focusMatch(index: number): void {
    if (matches.length === 0) return;
    const i = ((index % matches.length) + matches.length) % matches.length; // wrap
    const m = matches[i]!;
    setActive(i);
    setJumped(true);
    const page = pageTexts?.find((p) => p.pageIndex === m.pageIndex);
    setSearchHighlight({ pageIndex: m.pageIndex, rects: matchRects(page?.items, m.start, m.length) });
    scrollToPageIndex(m.pageIndex);
  }

  function step(delta: 1 | -1): void {
    if (matches.length === 0) return;
    focusMatch(jumped ? active + delta : active); // first step lands on the current match
  }

  return (
    <div className="search-bar" role="search">
      <div className="search-bar__row">
        <span className="search-bar__icon" aria-hidden="true">
          🔍
        </span>
        <input
          ref={inputRef}
          type="search"
          className="search-bar__input"
          placeholder="ドキュメント内を検索"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              step(e.shiftKey ? -1 : 1);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onClose();
            }
          }}
          aria-label="ドキュメント内を検索"
        />
        <span className="search-bar__count">
          {extracting ? '抽出中…' : query ? `${matches.length ? active + 1 : 0} / ${matches.length}` : ''}
        </span>
        <button
          className="btn btn--icon"
          disabled={matches.length === 0}
          onClick={() => step(-1)}
          title="前の一致 (Shift+Enter)"
          aria-label="前の一致"
        >
          ↑
        </button>
        <button
          className="btn btn--icon"
          disabled={matches.length === 0}
          onClick={() => step(1)}
          title="次の一致 (Enter)"
          aria-label="次の一致"
        >
          ↓
        </button>
        <button className="btn btn--icon" onClick={onClose} title="閉じる (Esc)" aria-label="検索を閉じる">
          ✕
        </button>
      </div>

      {error && <p className="search-bar__error">テキストを抽出できません: {error}</p>}

      {query && !extracting && matches.length === 0 && !error && (
        <p className="search-bar__empty">一致なし（保存前の追加テキストは保存・再オープン後に検索できます）</p>
      )}

      {matches.length > 0 && (
        <ul className="search-results">
          {matches.slice(0, 100).map((m, i) => (
            <li
              key={`${m.pageIndex}-${m.start}`}
              className={`search-results__item ${i === active && jumped ? 'is-current' : ''}`.trim()}
            >
              <button type="button" className="search-results__btn" onClick={() => focusMatch(i)}>
                <span className="search-results__page">p{m.pageIndex + 1}</span>
                <span className="search-results__snippet">
                  {m.snippet.slice(0, m.matchInSnippet[0])}
                  <mark>{m.snippet.slice(m.matchInSnippet[0], m.matchInSnippet[1])}</mark>
                  {m.snippet.slice(m.matchInSnippet[1])}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
